import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { ArrowRightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { getToken, removeToken, setToken } from "../utils/auth";
import { logout, resetAccountInfo } from "../actionCreator/commonActionCreator";
import { getSubscriptionUserInfo, getUserInfo } from "../utils/utils";
import { useAlert } from "./AlertProvider";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { permissions } = getUserInfo();
  const subscription = getSubscriptionUserInfo();
  const [isLight, setLight] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(getUserInfo());
  useEffect(() => {
    if (!getToken()) {
      return;
    }
    if (!subscription?.validTo) {
      return;
    }
    const getDaysLeft = (futureDate) => {
      const today = dayjs().startOf("day");
      const target = dayjs(futureDate).startOf("day");
      return target.diff(today, "day");
    };
    const hasShown = sessionStorage.getItem("trialModalShown");
    const isToday = dayjs(subscription?.validTo).isSame(dayjs(), "day");
    const daysLeft = getDaysLeft(subscription?.validTo);

    if (!hasShown && daysLeft < 8) {
      showAlert(
        "warning",
        isToday
          ? "Your free trial expires today. Subscribe now to avoid interruption."
          : daysLeft < 8 && daysLeft > -1
            ? `You have ${daysLeft} days remaining in your free trial`
            : "Your subscriptions is expired.",
        <div
          className="fw-semibold cursor-pointer text-decoration-underline d-flex align-items-center gap-10"
          onClick={() => navigate("/pricing")}
        >
          Subscribe Now <ArrowRightOutlined />
        </div>,
        10000,
      );
      sessionStorage.setItem("trialModalShown", "true");
    }
  }, [location, subscription]);

  const checkSession = () => {
    const session = JSON.parse(localStorage.getItem("auth"));

    const currentTime = new Date().getTime();

    if (currentTime > session?.expiresAt) {
      logout();
      return false;
    }

    return true;
  };

  useEffect(() => {
    setLight(localStorage.getItem("theme") === "dark" ? false : true);
    setCollapsed(
      localStorage.getItem("isCollapsed") === "false" ? false : true,
    );
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      checkSession();
    }, 1000 * 60);

    return () => clearInterval(interval);
  }, []);

  const login = (token, rememberMe, user, refreshToken) => {
    setToken(token, rememberMe, refreshToken);
    localStorage.setItem("theme", "light");
    setLight(true);
    setUser(user);
  };

  const isWebToLeadDelete = permissions?.["Web To Leads"]?.find(
    (d) => d?.name === "Delete",
  );
  const isWebToLeadCreate = permissions?.["Web To Leads"]?.find(
    (d) => d?.name === "Create",
  );
  const isWebToLeadEdit = permissions?.["Web To Leads"]?.find(
    (d) => d?.name === "Edit",
  );
  const isWebToLeadView = permissions?.["Web To Leads"]?.find(
    (d) => d?.name === "View",
  );

  const isRMView = permissions?.["Reputation Manager"]?.find(
    (d) => d?.name === "View",
  );

  const isCompanyView = permissions?.["Company"]?.find(
    (d) => d?.name === "View",
  );

  const isLeadCreate = permissions?.["Leads"]?.find(
    (d) => d?.name === "Create",
  );
  const isLeadEdit = permissions?.["Leads"]?.find((d) => d?.name === "Edit");
  const isLeadView = permissions?.["Leads"]?.find((d) => d?.name === "View");
  const isLeadCustomFieldView = permissions?.["Custom Fields"]?.find(
    (d) => d?.name === "View",
  );
  const isLeadCustomFieldCreate = permissions?.["Custom Fields"]?.find(
    (d) => d?.name === "Create",
  );
  const isLeadCustomFieldEdit = permissions?.["Custom Fields"]?.find(
    (d) => d?.name === "Edit",
  );
  const isLeadCustomFieldDelete = permissions?.["Custom Fields"]?.find(
    (d) => d?.name === "Delete",
  );

  const isExecutiveView = permissions?.["Executive"]?.find(
    (d) => d?.name === "View",
  );

  const isEmailSetupView = permissions?.["Email Setup"]?.find(
    (d) => d?.name === "View",
  );
  const isEmailSetupCreate = permissions?.["Email Setup"]?.find(
    (d) => d?.name === "Create",
  );

  const isKanbanView = permissions?.["Kanban Pipeline View"]?.find(
    (d) => d?.name === "View",
  );

  const whatsappKey = permissions ? Object.keys(permissions).find(k => k?.trim()?.toLowerCase() === "whatsapp") : null;
  const isWhatsAppView = !!(whatsappKey && permissions[whatsappKey] && permissions[whatsappKey].length > 0);
  const isWhatsAppCreate = !!(whatsappKey && permissions[whatsappKey]?.find(
    (d) => d?.name?.trim()?.toLowerCase() === "create"
  ));
  const isWhatsAppEdit = !!(whatsappKey && permissions[whatsappKey]?.find(
    (d) => d?.name?.trim()?.toLowerCase() === "edit" || d?.name?.trim()?.toLowerCase() === "update"
  ));

  const onLogout = async () => {
    dispatch(logout(getToken()));
    removeToken();
    dispatch(resetAccountInfo());
    setUser(null);
    navigate("/signin");
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!getToken(),
        login,
        logout: onLogout,
        user,
        setUser,
        subscription,
        isWebToLeadDelete,
        isWebToLeadCreate,
        isWebToLeadEdit,
        isLeadCreate,
        isRMView,
        isCompanyView,
        isLeadView,
        isExecutiveView,
        isEmailSetupView,
        isLeadEdit,
        isLeadCustomFieldView,
        isLeadCustomFieldCreate,
        isLeadCustomFieldEdit,
        isLeadCustomFieldDelete,
        isWebToLeadView,
        isEmailSetupCreate,
        isKanbanView,
        isWhatsAppView,
        isWhatsAppCreate,
        isWhatsAppEdit,
        permissions,
        isLight,
        collapsed,
        onToggleTheme: (val) => {
          localStorage.setItem("theme", val ? "light" : "dark");
          setLight(localStorage.getItem("theme") === "light" ? true : false);
        },
        onToggleSidebar: (val) => {
          localStorage.setItem("isCollapsed", val);
          setCollapsed(
            localStorage.getItem("isCollapsed") === "false" ? false : true,
          );
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
